import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'react-toastify';
import { Box, Button, Card, CardActionArea, CardContent, CircularProgress, Link, Typography } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ClientWorkspaceUnderstand from './ClientWorkspaceUnderstand';
import AddProspectModal from '../Prospects/AddProspectModal';
import { getAllCompanies } from '../../queries/companies';
import { getAllProducts } from '../../queries/products';
import { getProspectSubmissions, useCreateProspectSubmission } from '../../queries/prospectSubmissions';
import { prospectErrorMessage } from '../Prospects/prospectFields';
import {
  ENGAGE_AVAILABILITY,
  ENGAGE_COPY,
  getEngageAvailability,
  opportunityClientId,
  opportunityProgramId,
} from './engageAvailability';

const ACTION_TYPES = {
  UNDERSTAND: 'understand',
};

const cardSx = {
  borderRadius: 2,
  boxShadow: 'none',
  height: '100%',
  minHeight: 280,
  borderColor: 'divider',
  bgcolor: '#fff',
};

const ActionIcon = ({ children, bg, color }) => (
  <Box
    sx={{
      width: 48,
      height: 48,
      borderRadius: '50%',
      bgcolor: bg,
      color,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

const CardEyebrow = ({ children }) => (
  <Typography
    variant='caption'
    fontWeight={700}
    letterSpacing={0.6}
    sx={{ color: '#64748b', textTransform: 'uppercase', lineHeight: 1.4 }}
  >
    {children}
  </Typography>
);

const UnderstandCard = ({ companyName, onOpen }) => (
  <Card
    variant='outlined'
    sx={{
      ...cardSx,
      transition: 'background-color 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease',
      '&:hover': {
        borderColor: '#90caf9',
        bgcolor: '#f5f9ff',
        boxShadow: '0 8px 24px rgba(25, 118, 210, 0.12)',
        transform: 'translateY(-2px)',
      },
      '& .MuiCardActionArea-focusHighlight': {
        backgroundColor: 'transparent',
      },
    }}
  >
    <CardActionArea
      sx={{
        height: '100%',
        alignItems: 'stretch',
        '&:hover .MuiCardActionArea-focusHighlight': { opacity: 0 },
      }}
      onClick={onOpen}
    >
      <CardContent
        sx={{
          p: 2.5,
          minHeight: 280,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          '&:last-child': { pb: 2.5 },
        }}
      >
        <Box display='flex' alignItems='center' gap={1.5}>
          <ActionIcon bg='#e3f2fd' color='#1976d2'>
            <ChatBubbleOutlineIcon sx={{ fontSize: 24 }} />
          </ActionIcon>
          <CardEyebrow>Understand {companyName}</CardEyebrow>
        </Box>
        <Box flex={1}>
          <Typography variant='h6' fontWeight={700} mb={0.75} lineHeight={1.3}>
            Understand
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.6 }}>
            Ask anything about {companyName}. Get instant answers based on all available information, including
            products, ICP, positioning, competitors, resources, and more.
          </Typography>
        </Box>
        <Box display='flex' justifyContent='flex-end' mt='auto' pt={1}>
          <ChevronRightIcon
            fontSize='small'
            sx={{
              color: 'action.active',
              transition: 'color 0.2s ease, transform 0.2s ease',
              '.MuiCard-root:hover &': {
                color: 'primary.main',
                transform: 'translateX(2px)',
              },
            }}
          />
        </Box>
      </CardContent>
    </CardActionArea>
  </Card>
);

const EngageCard = ({ availability, isLoading, onAddProspect, onViewProspects, onOpen }) => {
  const approved = availability === ENGAGE_AVAILABILITY.APPROVED;
  const locked = !approved;

  const body = (
    <CardContent
      sx={{
        p: 2.5,
        minHeight: 280,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
        '&:last-child': { pb: 2.5 },
      }}
    >
      <Box display='flex' alignItems='center' gap={1.5} pr={locked ? 3 : 0}>
        <ActionIcon bg='#f3e5f5' color='#7b1fa2'>
          <PeopleAltOutlinedIcon sx={{ fontSize: 24 }} />
        </ActionIcon>
        <CardEyebrow>Engage with a prospect</CardEyebrow>
      </Box>
      <Box flex={1}>
        <Typography variant='h6' fontWeight={700} mb={0.75} lineHeight={1.3}>
          Engage
        </Typography>
        {isLoading ? (
          <Box display='flex' alignItems='center' py={1}>
            <CircularProgress size={22} />
          </Box>
        ) : (
          <Typography variant='body2' color='text.secondary' sx={{ lineHeight: 1.6 }}>
            {ENGAGE_COPY[availability]}
          </Typography>
        )}
      </Box>
      {isLoading ? null : approved ? (
        <Box display='flex' justifyContent='flex-end' mt='auto' pt={1}>
          <ChevronRightIcon fontSize='small' sx={{ color: 'action.active' }} />
        </Box>
      ) : (
        <Button
          variant='contained'
          fullWidth
          onClick={availability === ENGAGE_AVAILABILITY.PENDING ? onViewProspects : onAddProspect}
          sx={{ mt: 'auto', textTransform: 'none', fontWeight: 600 }}
        >
          {availability === ENGAGE_AVAILABILITY.PENDING ? 'View prospects' : 'Add prospect'}
        </Button>
      )}
    </CardContent>
  );

  return (
    <Card
      variant='outlined'
      sx={{
        ...cardSx,
        position: 'relative',
        borderColor: approved ? '#90caf9' : 'divider',
        bgcolor: approved ? '#f5f9ff' : '#fff',
        ...(approved
          ? {
              transition: 'box-shadow 0.2s ease, transform 0.2s ease',
              '&:hover': {
                boxShadow: '0 8px 24px rgba(25, 118, 210, 0.12)',
                transform: 'translateY(-2px)',
              },
            }
          : {}),
      }}
    >
      {locked ? (
        <LockOutlinedIcon
          sx={{ position: 'absolute', top: 18, right: 18, fontSize: 20, color: '#94a3b8' }}
          aria-hidden
        />
      ) : null}
      {approved && onOpen ? (
        <CardActionArea onClick={onOpen} sx={{ height: '100%', '& .MuiCardActionArea-focusHighlight': { opacity: 0 } }}>
          {body}
        </CardActionArea>
      ) : (
        body
      )}
    </Card>
  );
};

const ClientWorkspaceAI = ({ companyName = 'this company', product, company }) => {
  const navigate = useNavigate();
  const [activeAction, setActiveAction] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const clientId = opportunityClientId(product, company);
  const programId = opportunityProgramId(product);

  const { data: companies } = useQuery(getAllCompanies({}));
  const { data: products } = useQuery(getAllProducts({}));
  const { data: prospects, isLoading: isLoadingProspects } = useQuery(getProspectSubmissions());
  const { mutate: createProspect, isPending: isCreating } = useCreateProspectSubmission();

  const availability = getEngageAvailability(prospects, clientId, programId);

  if (activeAction === ACTION_TYPES.UNDERSTAND) {
    return (
      <ClientWorkspaceUnderstand
        companyName={companyName}
        productName={product?.productName || 'this product'}
        clientId={clientId}
        programId={programId}
        onBack={() => setActiveAction(null)}
      />
    );
  }

  const openEngage = () => {
    if (!clientId || !programId) {
      toast('This opportunity is missing a client or program.', { type: 'error' });
      return;
    }
    navigate(`/engage?clientId=${encodeURIComponent(clientId)}&programId=${encodeURIComponent(programId)}`);
  };

  const openAddProspect = () => {
    if (!clientId || !programId) {
      toast('This opportunity is missing a client or program.', { type: 'error' });
      return;
    }
    setAddOpen(true);
  };

  const handleCreate = (values) => {
    createProspect(
      { data: values },
      {
        onSuccess: () => {
          toast('Prospect submitted for approval.', { type: 'success' });
          setAddOpen(false);
        },
        onError: (error) => {
          toast(prospectErrorMessage(error, 'Could not submit this prospect.'), { type: 'error' });
        },
      },
    );
  };

  return (
    <Box display='flex' flexDirection='column' height='100%' minHeight={0}>
      <Box flex={1} overflow='auto' px={6} pt={5} pb={2} minHeight={0}>
        <Box textAlign='center' mb={3}>
          <Typography variant='h5' fontWeight={700} mb={0.75}>
            How can I help you today?
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Get insights about {companyName}, or create and refine prospect messages.
          </Typography>
        </Box>

        <Box display='grid' gridTemplateColumns={{ xs: '1fr', sm: 'repeat(2, 1fr)' }} gap={2} mb={4}>
          <UnderstandCard companyName={companyName} onOpen={() => setActiveAction(ACTION_TYPES.UNDERSTAND)} />
          <EngageCard
            availability={availability}
            isLoading={isLoadingProspects}
            onAddProspect={openAddProspect}
            onViewProspects={() => navigate('/prospects')}
            onOpen={openEngage}
          />
        </Box>

        <Box
          mt={6}
          px={3}
          py={2}
          borderRadius={2}
          border={1}
          borderColor='divider'
          bgcolor='#eef5ff'
          display='flex'
          alignItems='flex-start'
          gap={1.5}
          flexShrink={0}
        >
          <AutoAwesomeIcon color='primary' sx={{ fontSize: 22, mt: 0.25 }} />
          <Box>
            <Typography variant='body2' fontWeight={700} sx={{ mb: 1 }}>
              AI Assistant uses all available information about {companyName}
            </Typography>
            <Typography variant='body2' color='text.secondary' mb={0.5}>
              Summary, products, ICP, markets, competitors, resources and more.
            </Typography>
            <Link
              component='button'
              type='button'
              underline='hover'
              variant='body2'
              fontWeight={600}
              onClick={() => {}}
              sx={{ border: 0, background: 'none', cursor: 'pointer', p: 0 }}
            >
              Learn more
            </Link>
          </Box>
        </Box>
      </Box>
      <AddProspectModal
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSubmit={handleCreate}
        isSubmitting={isCreating}
        companies={companies}
        products={products}
        lockedClientId={clientId}
        lockedProgramId={programId}
      />
    </Box>
  );
};

export default ClientWorkspaceAI;
